/**
 * Canvas rescale — the math behind the reviewer's per-post "Format" switch
 * (setPresetCanvas). Pure ESM, client-only.
 *
 * Layers map PROPORTIONALLY per axis (x/width by sx, y/height by sy): that is the
 * only rule that preserves the COMPOSITION across an aspect change — a full-bleed
 * cover photo stays full-bleed (its cover fit re-crops, glyph-true), a bottom
 * scrim keeps covering the same fraction of the canvas, and the text sitting ON
 * that scrim stays on it. Per-layer "smart" re-anchoring (uniform scale + nearest
 * edge) was tried and rejected: it tears grouped layers apart — the headline
 * drifts off its scrim — because layers are positioned relative to each other,
 * not to the canvas edges. Nothing that draws glyphs distorts under the
 * proportional map: text/badge fontSize and padding scale by the UNIFORM
 * s = min(sx, sy), and cover images re-crop.
 *
 * The one real proportional artifact is an aspect-locked `contain` image (logo,
 * location pin): its BOX would stretch around the undistorted glyph, leaving
 * selection handles hugging empty air. Those layers instead scale uniformly by s
 * and anchor at the proportional CENTER — which is exactly where the glyph would
 * have rendered inside the stretched box, so the visual position is unchanged
 * while the box stays true to the glyph.
 *
 * Geometry is rounded to ints and sizes clamped ≥1 so the result round-trips
 * server-side validateDesignDoc.
 */

// Clamp a scaled size ≥1 — but preserve an exact 0 (a badge's auto-size sentinel).
const roundSize = v => (v === 0 ? 0 : Math.max(1, Math.round(v)));

// Uniform box anchored at the proportional center — for aspect-locked layers.
const uniformAxis = (pos, size, from, to, axisScale, s) => {
    if (typeof pos !== 'number' || typeof size !== 'number') return { pos, size };
    const center = (pos + size / 2) * axisScale;
    const scaled = size * s;
    return { pos: Math.round(center - scaled / 2), size: roundSize(scaled) };
};

/**
 * Rescale a design doc to a new canvas size, preserving the composition.
 * Returns a NEW doc (input untouched) that passes validateDesignDoc.
 */
export const rescaleDocToCanvas = (doc, width, height) => {
    const sx = width / doc.width;
    const sy = height / doc.height;
    const s = Math.min(sx, sy);
    const scaleNum = (v, k) => (typeof v === 'number' ? Math.round(v * k) : v);
    return {
        ...doc,
        width,
        height,
        layers: (doc.layers || []).map(l => {
            const aspectLocked = l.type === 'image' && l.fit === 'contain';
            let next;
            if (aspectLocked) {
                const ax = uniformAxis(l.x, l.width, doc.width, width, sx, s);
                const ay = uniformAxis(l.y, l.height, doc.height, height, sy, s);
                next = { ...l, x: ax.pos, y: ay.pos, width: ax.size, height: ay.size };
            } else {
                next = {
                    ...l,
                    x: scaleNum(l.x, sx),
                    y: scaleNum(l.y, sy),
                    width: typeof l.width === 'number' ? roundSize(l.width * sx) : l.width,
                    height: typeof l.height === 'number' ? roundSize(l.height * sy) : l.height,
                };
            }
            if (typeof l.fontSize === 'number')
                next.fontSize = Math.max(1, scaleNum(l.fontSize, s));
            if (typeof l.paddingX === 'number') next.paddingX = scaleNum(l.paddingX, s);
            if (typeof l.paddingY === 'number') next.paddingY = scaleNum(l.paddingY, s);
            return next;
        }),
    };
};
